import { getApplyPartnershipFunction } from '@easy-expense/auth-client';
import { useCachedOrg, useUserSettingsStore } from '@easy-expense/data-firestore-client';
import { Organization } from '@easy-expense/data-schema-v2';
import { ErrorHandling } from '@easy-expense/error-mobile';
import { Layout, OpenSans } from '@easy-expense/ui-web-core';
import React from 'react';
import { useAuthState } from 'react-firebase-hooks/auth';
import { useLocation, useNavigate, useSearchParams } from 'react-router-dom';

import { LoginForm } from '../components/Login.form';
import {
  OnboardingActionColumn,
  OnboardingBackdrop,
} from '../components/Onboarding/OnboardingBackdrop.component';
import { auth } from '../firebase/app';
import { useIsMobile } from '../hooks/useWindow';

// Fallback delay before redirecting without the org loaded (treat as non-partnership).
const ORG_LOAD_FALLBACK_MS = 4000;

export const Login: React.FC = () => {
  const navigate = useNavigate();
  const [user, loading] = useAuthState(auth);
  const [searchParams] = useSearchParams();

  const location = useLocation();

  const org = useCachedOrg() as Organization;
  // useCachedOrg returns a placeholder ({ key: undefined }) until orgs load; a truthy key
  // doubles as our "org loaded" guard so we can read partnership status without a flash.
  const orgKey = org.key;

  const userData = useUserSettingsStore((state) => state.userData);
  const updateUserSetting = useUserSettingsStore((state) => state.updateUserSetting);
  // Gate a plain login's redirect on this so the download page is only shown once — after
  // that, a partnership account logs in like anyone else and lands on their dashboard.
  const downloadPageAlreadyShown = !!userData?.settings.partnershipDownloadPageShown;

  const from = location.state?.from?.pathname || '/transactions';
  // The partner name is present on the URL only when arriving via the partner link.
  const partner = searchParams.get('partner');

  const handledRef = React.useRef(false);

  React.useEffect(() => {
    if (!user || handledRef.current) {
      return;
    }

    const resolveDestination = async () => {
      if (handledRef.current) {
        return;
      }
      handledRef.current = true;

      // Arrived via the partner link: convert the account (idempotent, paid-safe) and route
      // to the download page unless it was a paid/ineligible account. A repeat visit via the
      // link always redirects again (FR-015 idempotency) regardless of downloadPageAlreadyShown.
      let goPartner = false;
      if (partner?.trim() && orgKey) {
        try {
          const result = await getApplyPartnershipFunction()({
            organizationID: orgKey,
            partner,
          });
          const reason = result?.data?.reason;
          goPartner = result?.data?.converted === true || reason === 'already_partnership';
        } catch (error) {
          // Don't strand the user on the loading screen if the conversion call fails —
          // fall back to the default destination like a normal login.
          ErrorHandling.report(error);
        }
      } else {
        // Plain login: redirect a partnership account to the download page exactly once —
        // after that first login, it behaves like any other account.
        goPartner = org.isPartnership === true && !downloadPageAlreadyShown;
      }

      if (goPartner) {
        if (!downloadPageAlreadyShown) {
          void updateUserSetting({ partnershipDownloadPageShown: true });
        }
        const search = partner?.trim() ? `?partner=${encodeURIComponent(partner)}` : '';
        navigate({ pathname: '/app-download', search }, { replace: true });
      } else {
        navigate({ pathname: from, search: searchParams.toString() }, { replace: true });
      }
    };

    // Wait for the org (and user settings, so the "already shown" flag is accurate) to load
    // before deciding; fall back to the default destination if it never resolves.
    if (orgKey && userData) {
      void resolveDestination();
      return;
    }
    const timeout = setTimeout(() => void resolveDestination(), ORG_LOAD_FALLBACK_MS);
    return () => clearTimeout(timeout);
  }, [user, orgKey, userData]);

  const onSuccess = () => {
    // Post-auth navigation is handled by the effect above once auth + org are ready.
  };

  const isMobile = useIsMobile();

  // Once authenticated, the effect above is always navigating away (to /app-download or the
  // default destination) — keep showing this loading state instead of the login form so an
  // already-authenticated visitor (or one who just signed in) never sees stale form UI.
  if (loading || user) {
    return (
      <Layout.Column style={{ minHeight: '100vh' }} bg="brandPrimaryXLight" center>
        <OpenSans.Primary
          size={isMobile ? 'l-20' : '2xl-32'}
          weight="light-300"
          style={{ alignSelf: 'center' }}
        >
          Loading...
        </OpenSans.Primary>
      </Layout.Column>
    );
  }

  return (
    <OnboardingBackdrop>
      <OnboardingActionColumn width={430}>
        <LoginForm onSuccess={onSuccess} />
      </OnboardingActionColumn>
    </OnboardingBackdrop>
  );
};
